type Money = { amount: number; currency: string };

/**
 * Every price on the platform is pinned to en-US regardless of the viewer's
 * locale: the webapp formats on both the server and the browser, and any
 * locale drift between them is a React hydration mismatch.
 */
const LOCALE = 'en-US';

/**
 * How many fraction digits a price keeps. `cents` always shows both digits,
 * `headline` drops them from whole amounts, `rounded` keeps at most one and
 * never pads.
 */
type Precision = 'headline' | 'cents' | 'rounded';

// Not typed as Intl.NumberFormatOptions: `trailingZeroDisplay` is newer than
// the TS lib. Spread into the options literal, it type-checks as an extra.
const PRECISION_OPTIONS = {
  headline: { trailingZeroDisplay: 'stripIfInteger' },
  cents: {},
  rounded: { minimumFractionDigits: 0, maximumFractionDigits: 1 },
} satisfies Record<Precision, object>;

const formatters = new Map<string, Intl.NumberFormat>();

const getFormatter = (currency: string, precision: Precision): Intl.NumberFormat | null => {
  const key = `${currency}|${precision}`;
  let formatter = formatters.get(key);
  if (formatter === undefined) {
    try {
      formatter = new Intl.NumberFormat(LOCALE, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        ...PRECISION_OPTIONS[precision],
      });
    } catch {
      return null;
    }
    formatters.set(key, formatter);
  }
  return formatter;
};

const format = ({ amount, currency }: Money, precision: Precision): string => {
  const formatter = getFormatter(currency, precision);
  if (formatter === null) {
    const keepsCents = precision === 'cents' || (precision === 'headline' && amount % 1 !== 0);
    const number = new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: keepsCents ? 2 : 0,
      maximumFractionDigits: precision === 'rounded' ? 1 : 2,
    }).format(amount);
    return `${currency.toUpperCase()} ${number}`;
  }
  return formatter.format(amount);
};

/**
 * Headline prices (storefront cards, offers): whole amounts stay clean —
 * "$100", not "$100.00" — and fractional ones keep both cents digits.
 */
export const formatPrice = (money: Money): string => format(money, 'headline');

/**
 * Itemised money (receipts, ledgers, emails): always two cents digits, so
 * "$100.00" lines up beside "$99.50" instead of reading like a typo.
 */
export const formatMoney = (money: Money): string => format(money, 'cents');

/**
 * The number half of a scaled-down figure ("$2.9" for 2.9K): one decimal at
 * most, never padded — a second digit is noise at that magnitude.
 */
export const formatPriceRounded = (money: Money): string => format(money, 'rounded');
